import { useState, useEffect, useCallback, useRef } from 'react'; // useRefを追加
import { toast } from 'react-hot-toast';
import { Search } from 'lucide-react';
import { UserRole } from '../../../types/UserRole';
import { UserPermission, UserPermissionLabels } from '../../../types/UserPermission';
import type { UserMasterInfo, UserMasterCreateRequest, UserMasterUpdateRequest } from '../../../types';
import { useUsersApi } from '../../../hooks/useUsersApi';

// カスタムフック、検索ダイアログ、定数のインポート
import { MasterSearchDialog } from '../../common/MasterSearch/MasterSearchDialog'; 
import { useUserMasterCodeName } from '../../../hooks/useUserMasterCodeName';
import { MasterCategoryConst, type MasterCategoryType } from '../../../types/masterSearchTypes';

// --- 内部用変換メソッド ---

/**
 * 数値を4桁の0埋め文字列に変換
 */
const formatToPaddedCode = (value: number | null): string => {
  if (value === null) return "";
  return String(value).padStart(4, "0");
};

/**
 * 生産者用のコードを作成 (0000_0000 形式)
 */
const formatFarmerCode = (seller: number | null, farmer: number | null): string => {
  if (seller === null || farmer === null) return "";
  const s = String(seller).padStart(4, "0");
  const f = String(farmer).padStart(4, "0");
  return `${s}_${f}`;
};

/**
 * プロパティの型定義
 */
interface Props {
  originalUser: UserMasterInfo;
  onSave: (u: UserMasterCreateRequest | UserMasterUpdateRequest) => void;
  onCancel: () => void;
  isCreating?: boolean; // 新規登録モードかどうか
  isSaving?: boolean;   // 保存処理中（ボタン無効化用）
}

/**
 * ユーザー情報の登録・編集フォーム コンポーネント
 * 役割（Role）に応じて、マスタコード（売手・生産者・買手）の入力を制御
 */
export const UserMaintenanceEditForm = ({ 
  originalUser, 
  onSave, 
  onCancel, 
  isCreating = false, 
  isSaving = false 
}: Props) => {

  // 採番用API
  const { generateLoginId } = useUsersApi();

  // --- 1. States：基本情報を管理 ---
  // フォーム全体の入力値を保持
  const [formData, setFormData] = useState<UserMasterInfo>({ ...originalUser });

  // --- 2. Custom Hook：名称取得を一任 ---
  // roleとコードに基づいて、表示用の名称（names）を自動取得・管理
  const { codes, setCodes, names } = useUserMasterCodeName(formData.role, originalUser.masterCode || '');

  /**
   * Role変更監視フラグ
   * 役割が変わった直後、namesが更新されるまで古い名称をセットさせないためのフラグ
   */
  const isRoleChangedRef = useRef(false);

  // --- 採番実行関数 ---
  const fetchNextId = useCallback(async (role: UserRole, mCode: string) => {

    if (!isCreating || !mCode) {
      setFormData(prev => ({ ...prev, loginId: '' }));
      return;
    }

    try {
      const nextId = await generateLoginId(role, mCode);
      if (nextId) {
        setFormData(prev => ({ ...prev, loginId: nextId }));
      }
    } catch (err) {
      console.error("採番失敗:", err);
      setFormData(prev => ({ ...prev, loginId: '' }));
    }
  }, [isCreating, generateLoginId]);

  // --- 自動採番 useEffect ---
  useEffect(() => {
    if (!isCreating) return;
    
    // 市場(Admin)の場合は handleRoleChange 側で完結しているため、
    // ここで処理を重ねると古いステートで上書きされるリスクがあるので除外。
    if (formData.role === UserRole.Admin) return;

    // 現在の入力状況に基づいて採番
    if (formData.role === UserRole.Seller && codes.seller !== null) {
      fetchNextId(UserRole.Seller, formatToPaddedCode(codes.seller));
    } 
    else if (formData.role === UserRole.Buyer && codes.buyer !== null) {
      fetchNextId(UserRole.Buyer, formatToPaddedCode(codes.buyer));
    } 
    else if (formData.role === UserRole.Farmer && codes.seller !== null && codes.farmer !== null) {
      fetchNextId(UserRole.Farmer, formatFarmerCode(codes.seller, codes.farmer));
    }    
  }, [isCreating, formData.role, codes.seller, codes.farmer, codes.buyer, fetchNextId]);

  // --- 役割変更ハンドラ ---
  const handleRoleChange = (newRole: UserRole) => {
    /**
     * 役割変更フラグを立てることで、useEffect側での「古いnamesによる上書き」をブロック。
     */
    isRoleChangedRef.current = true;

    // 1. 基本情報の初期化（氏名・ログインIDをリセット）
    setFormData(prev => ({ 
      ...prev, 
      role: newRole, 
      loginId: '', 
      username: '' 
    }));
    
    // 2. マスタコード情報の初期化
    setCodes({ seller: null, farmer: null, buyer: null });

    // 3. 採番およびAdmin用の特別処理
    let nextMasterCode = '';
    if (newRole === UserRole.Admin) {
      nextMasterCode = "8888";
      // 市場に切り替えた場合、画面表示用に「8888」をセット
      setCodes({ seller: 8888, farmer: 8888, buyer: null });
      // Adminの場合は即座に名称確定を期待するため、フラグを倒しておく（またはフック側の更新を待つ）
      isRoleChangedRef.current = false; 
    }

    // 4. 採番実行
    fetchNextId(newRole, nextMasterCode);
  };

  // --- 初期表示（マウント時）の制御 ---
  useEffect(() => {
    if (isCreating && formData.role === UserRole.Admin) {
      setCodes({ seller: 8888, farmer: 8888, buyer: null });
      fetchNextId(UserRole.Admin, "8888");
    }
  }, []);

  // --- 3. 検索ダイアログ制御用の状態 ---
  const [isSearchOpen, setIsSearchOpen] = useState(false); // ダイアログの開閉
  const [searchCategory, setSearchCategory] = useState<MasterCategoryType>(MasterCategoryConst.SELLER); // 検索対象マスタ
  const [activeTarget, setActiveTarget] = useState<'seller' | 'farmer' | 'buyer'>('seller'); // どの入力欄に対する検索か

  // --- 4. スタイル定義（Tailwind CSS） ---
  const inputClasses = "mt-1 w-full p-2 border border-slate-300 rounded-md bg-yellow-50 focus:ring-indigo-500 focus:border-indigo-500 text-sm h-10 disabled:bg-slate-200 disabled:cursor-not-allowed transition-colors shadow-sm";
  const inputLabelClasses = "block text-xs font-bold text-slate-500 mb-1";
  
  // マスタコード入力欄専用スタイル（左側のみ角丸、検索ボタンと結合するため）
  const codeInputClasses = "mt-1 w-20 p-2 border border-slate-300 rounded-l-md bg-yellow-50 focus:ring-1 focus:ring-indigo-500 text-sm h-10 outline-none shadow-sm disabled:bg-slate-200 disabled:cursor-not-allowed text-left";

  // --- 5. Handlers ---

  /** 文字列入力の汎用ハンドラ（最大文字数制限付き） */
  const handleStringInput = (val: string, maxLength: number, key: keyof UserMasterInfo) => {
    setFormData(p => ({ ...p, [key]: val.substring(0, maxLength) }));
  };

  /** 数字のみを許可し、最大4桁に制限するマスタコード専用ハンドラ */
  const handleNumericCodeInput = (val: string, key: 'seller' | 'farmer' | 'buyer') => {
    const numericVal = val.replace(/[^0-9]/g, ''); // 数字以外を空文字に置換
    const num = numericVal === "" ? null : Number(numericVal.substring(0, 4));
    setCodes(p => ({ ...p, [key]: num })); 
  };

  /** 最大同時接続数の入力ハンドラ（1〜99の範囲制限） */
  const handleSessionCountInput = (val: string) => {
    if (val === "") {
      setFormData(p => ({ ...p, maxSessionCount: "" as any }));
      return;
    }
    const num = Number(val);
    if (num >= 1 && num <= 99) {
      setFormData(p => ({ ...p, maxSessionCount: num }));
    }
  };

  /** マスタ検索ダイアログを表示する */
  const openSearch = (cat: MasterCategoryType, target: 'seller' | 'farmer' | 'buyer') => {
    if (formData.role === UserRole.Admin) return; // 市場ユーザーはコード固定のため検索不可
    
    // 生産者検索は、親となる売手コードが入力されている場合のみ許可
    if (target === 'farmer' && codes.seller === null) return;

    setSearchCategory(cat);
    setActiveTarget(target);
    setIsSearchOpen(true);
  };

  /** ダイアログでマスタを選択した際の処理 */
  const handleSelectMaster = (selectedData: any) => {
    if (formData.role === UserRole.Admin || formData.role === UserRole.Farmer) {
      if (activeTarget === 'farmer') {
        // 生産者選択時：選択した生産者に紐づく売手コードも反映
        setCodes({
          seller: selectedData.sellerCode ?? codes.seller,
          farmer: selectedData.farmerCode ?? null,
          buyer: null
        });
        // 生産者選択時は氏名を上書き
        setFormData(prev => ({ ...prev, username: selectedData.farmerName || prev.username }));
      } else {
        // 売手選択時：整合性を保つため生産者コードをクリア
        setCodes(prev => ({ 
          ...prev, 
          seller: selectedData.sellerCode ?? null,
          farmer: null 
        }));
        if (formData.role !== UserRole.Farmer) {
          setFormData(prev => ({ ...prev, username: selectedData.sellerName || prev.username }));
        }
      }
    } else if (formData.role === UserRole.Seller) {
      setCodes(prev => ({ ...prev, seller: selectedData.sellerCode ?? null }));
      setFormData(prev => ({ ...prev, username: selectedData.sellerName || prev.username }));
    } else if (formData.role === UserRole.Buyer) {
      setCodes(prev => ({ ...prev, buyer: selectedData.buyerCode ?? null }));
      setFormData(prev => ({ ...prev, username: selectedData.buyerName || prev.username }));
    }
    setIsSearchOpen(false);
  };

  // --- 6. 保存時バリデーション ---
  const validate = (): boolean => {
    const { loginId, username, maxSessionCount, role } = formData;
    const errors: string[] = [];

    // 必須項目チェック
    if (!loginId?.toString().trim()) errors.push("・ログインIDを入力してください");
    if (!username?.trim()) errors.push("・氏名を入力してください");
    if (!maxSessionCount?.toString().trim()) errors.push("・最大同時接続数を入力してください");

    // 生産者モード時のバリデーション
    // システム仕様上、売手と生産者はセット（両方あり or 両方空）である必要がある
    if (role === UserRole.Farmer) {
      const s = codes.seller;
      const f = codes.farmer;
      if ((s !== null && f === null) || (s === null && f !== null)) {
        errors.push("・売手と生産者は両方入力するか、両方空にしてください");
      }
    }

    // ログインIDの形式チェック（半角英数記号）
    if (loginId && !/^[!-~]+$/.test(loginId)) {
      errors.push("・ログインIDは半角英数のみ使用可能です。");
    }

    // エラーがある場合はトースト表示して中断
    if (errors.length > 0) {
      toast.error(
        <div className="text-left whitespace-pre-wrap">
          <div className="font-bold mb-1 underline">入力内容を確認してください</div>
          {errors.join('\n')}
        </div>
      );
      return false;
    }
    return true;
  };

  /** 内部的な保存処理（バリデーション後に親のonSaveを呼ぶ） */
  const handleInternalSave = () => {
    if (!validate()) return;

    // 役割に応じて masterCode を構築
    let finalMasterCode = "";
     if (formData.role === UserRole.Farmer) {
      finalMasterCode = formatFarmerCode(codes.seller, codes.farmer);
    } else if (formData.role === UserRole.Admin ||formData.role === UserRole.Seller) {
      finalMasterCode = formatToPaddedCode(codes.seller);
    } else if (formData.role === UserRole.Buyer) {
      finalMasterCode = formatToPaddedCode(codes.buyer);
    }

    // 保存用リクエストオブジェクトの構築
    const { username, ...rest } = formData;
    const request = {
      ...rest,
      userName: username,
      masterCode: finalMasterCode,
      maxSessionCount: Number(formData.maxSessionCount) || 1,
    };

    onSave(request as any);
  };

  // --- 7. レンダリング ---
  return (
    <div className="bg-white p-6 rounded-lg border shadow-sm max-w-4xl mx-auto">
      {/* フォーム見出し */}
      <h3 className="text-lg font-bold text-slate-800 mb-6 border-b pb-2">
        {isCreating ? '新規ユーザー登録' : 'ユーザー情報の編集'}
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        {/* 基本情報エリア：役割選択 */}
        <div>
          <label className={inputLabelClasses}>役割</label>
          <select 
            value={formData.role} 
            onChange={(e) => handleRoleChange(e.target.value as UserRole)} 
            disabled={!isCreating} // 役割は作成後変更不可
            className={`${inputClasses} ${isCreating ? 'cursor-pointer' : ''}`}
          >
            <option value={UserRole.Admin}>市場</option>
            <option value={UserRole.Seller}>売手</option>
            <option value={UserRole.Farmer}>生産者</option>
            <option value={UserRole.Buyer}>買手</option>
          </select>
        </div>
        <div></div>
        {/* マスタコードセクション：役割によって表示を切り替え */}
        {(formData.role === UserRole.Admin || formData.role === UserRole.Farmer) ? (
          <>
            {/* 売手入力（市場・生産者用） */}
            <div>
              <label className={inputLabelClasses}>売手</label>
              <div className="flex items-center gap-0">
                <input
                  type="text"
                  value={codes.seller ?? ''}
                  onChange={(e) => handleNumericCodeInput(e.target.value, 'seller')}
                  disabled={formData.role === UserRole.Admin}
                  className={codeInputClasses}
                />
                <button 
                  type="button"
                  disabled={formData.role === UserRole.Admin}
                  onClick={() => openSearch(MasterCategoryConst.SELLER, 'seller')} 
                  className="mt-1 px-3 bg-slate-100 border border-l-0 border-slate-300 rounded-r-md hover:bg-slate-200 h-10 flex items-center justify-center transition-colors disabled:bg-slate-200"
                >
                  <Search size={16} className="text-slate-500" />
                </button>
                <div className="ml-3 mt-1 flex-1 text-sm text-slate-600 truncate font-medium border-b border-dotted border-slate-300 pb-1 h-8 self-end">
                  {names.seller}
                </div>
              </div>
            </div>

            {/* 生産者入力 */}
            <div>
              <label className={inputLabelClasses}>生産者</label>
              <div className="flex items-center gap-0">
                <input
                  type="text"
                  value={codes.farmer ?? ''}
                  onChange={(e) => handleNumericCodeInput(e.target.value, 'farmer')}
                  disabled={formData.role === UserRole.Admin || codes.seller === null}
                  className={codeInputClasses}
                />
                <button 
                  type="button"
                  disabled={formData.role === UserRole.Admin || codes.seller === null}
                  onClick={() => openSearch(MasterCategoryConst.FARMER, 'farmer')} 
                  className="mt-1 px-3 bg-slate-100 border border-l-0 border-slate-300 rounded-r-md hover:bg-slate-200 h-10 flex items-center justify-center transition-colors disabled:bg-slate-200"
                >
                  <Search size={16} className="text-slate-500" />
                </button>
                <div className="ml-3 mt-1 flex-1 text-sm text-slate-600 truncate font-medium border-b border-dotted border-slate-300 pb-1 h-8 self-end">
                  {names.farmer}
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* 単一マスタ入力（売手単体 or 買手用） */}
            <div>
              <label className={inputLabelClasses}>{formData.role === UserRole.Seller ? '売手' : '買手'}</label>
              <div className="flex items-center gap-0">
                <input 
                  type="text" 
                  value={(formData.role === UserRole.Seller ? codes.seller : codes.buyer) ?? ''} 
                  onChange={(e) => handleNumericCodeInput(
                    e.target.value, 
                    formData.role === UserRole.Seller ? 'seller' : 'buyer'
                  )} 
                  className={codeInputClasses} 
                />
                <button 
                  type="button" 
                  onClick={() => openSearch(
                    formData.role === UserRole.Seller ? MasterCategoryConst.SELLER : MasterCategoryConst.BUYER, 
                    formData.role === UserRole.Seller ? 'seller' : 'buyer'
                  )} 
                  className="mt-1 px-3 bg-slate-100 border border-l-0 border-slate-300 rounded-r-md hover:bg-slate-200 h-10 flex items-center justify-center transition-colors"
                >
                  <Search size={16} />
                </button>
                <div className="ml-3 mt-1 flex-1 text-sm text-slate-600 truncate font-medium border-b border-dotted border-slate-300 pb-1 h-8 self-end">
                  {formData.role === UserRole.Seller ? names.seller : names.buyer}
                </div>
              </div>
            </div>
            <div className="hidden md:block"></div>
          </>
        )}
        {/* ユーザー詳細情報エリア */}
        <div>
          <label className={inputLabelClasses}>ログインID</label>
          <input type="text" value={formData.loginId} disabled={true} className={inputClasses} />
        </div>
        <div>
          <label className={inputLabelClasses}>氏名</label>
          <input type="text" value={formData.username} onChange={(e) => handleStringInput(e.target.value, 40, 'username')} className={inputClasses} />
        </div>
        
        <div>
          <label className={inputLabelClasses}>権限</label>{/* 管理者はコンボに表示しない*/}
          <select value={formData.permission} onChange={(e) => setFormData(p => ({ ...p, permission: Number(e.target.value) as UserPermission }))} className={`${inputClasses} cursor-pointer`}>
            {(Object.values(UserPermission).filter(v => typeof v === 'number').filter(v => v !== UserPermission.Admin) as UserPermission[]).map(v => (
              <option key={v} value={v}>{UserPermissionLabels[v]}</option>
            ))}
          </select>
        </div>

        <div>
          <label className={inputLabelClasses}>最大同時接続数</label>
          <input type="number" value={formData.maxSessionCount} onChange={(e) => handleSessionCountInput(e.target.value)} placeholder="1～99" className={inputClasses} />
        </div>

        {/* 無効化（isDeleted）制御エリア */}
        {(originalUser.isDeleted || formData.isDeleted) ? (
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <span className="text-sm font-bold text-slate-500">無効化</span>
              <input type="checkbox" checked={!!formData.isDeleted} onChange={(e) => setFormData(p => ({ ...p, isDeleted: e.target.checked }))} className="w-5 h-5 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 cursor-pointer" />
            </label>
          </div>
        ) : (
          <div className="hidden md:block"></div>
        )}
      </div>

      {/* アクションボタン */}
      <div className="flex justify-end gap-3 mt-10 pt-6 border-t border-slate-100">
        <button type="button" onClick={onCancel} className="px-8 py-2 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 text-sm font-medium">
          キャンセル
        </button>
        <button 
          type="button" 
          onClick={handleInternalSave} 
          disabled={isSaving} 
          className="px-10 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-300 text-sm font-bold shadow-md"
        >
          {isSaving ? '保存中...' : (isCreating ? '登録' : '更新')}
        </button>
      </div>

      {/* 汎用マスタ検索ダイアログ */}
      <MasterSearchDialog 
        isOpen={isSearchOpen} 
        onClose={() => setIsSearchOpen(false)} 
        category={searchCategory} 
        initialValues={{ sellerCode: codes.seller, sellerName: names.seller }} 
        onSelect={handleSelectMaster} 
      />
    </div>
  );
};